<script setup lang="ts">
import type { DadataParams, Suggestion } from '@/js/types/dadata';
import { abort, getAddressSuggests } from '@/js/api/dadata';
import { classname } from '@/js/utils.js';
import BaseLoader from '@/js/vue/base/base-loader.vue';
import { useDebounceFn } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  modelValue: string
  params: DadataParams
  callback?: ((value: string) => string | null)
  placeholder: string
  required?: boolean
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'update:suggestions', value: Suggestion[]): void
  (e: 'select', value: Suggestion): void
  (e: 'blur'): void
}>();

const cn = classname('address-suggestions');

const suggestions = ref<Suggestion[]>([]);
const suggestionIndex = ref<number | null>(null);
const inputQuery = ref(props.modelValue);
const loading = ref(false);
const currentSuggestion = ref<Suggestion | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const sliceSuggestions = computed(() => {
  if (props.callback) {
    return suggestions.value.map(item => props.callback?.(item.value));
  }
  return suggestions.value.map(item => item.value);
});

// Fetch
async function fetchSuggestions(value: string) {
  if (loading.value) {
    abort();
  }
  loading.value = true;
  suggestionIndex.value = null;

  try {
    const { data } = await getAddressSuggests(value, props.params);
    if (data?.suggestions) {
      suggestions.value = data.suggestions;
      emit('update:suggestions', suggestions.value);
    }
    else {
      suggestions.value = [];
    }
  }
  catch (e) {
    console.log(e);
  }
  finally {
    loading.value = false;
  }
}

const debouncedFetchSuggestions = useDebounceFn(fetchSuggestions, 300);

// Watchers
watch(inputQuery, debouncedFetchSuggestions);
watch(inputQuery, () => {
  emit('update:modelValue', inputQuery.value);
});
watch(() => props.modelValue, () => {
  inputQuery.value = props.modelValue;
});
watch(currentSuggestion, () => {
  if (props.callback) {
    inputQuery.value = props.callback(currentSuggestion.value?.value || '') || '';
  }
  else {
    inputQuery.value = currentSuggestion.value?.value || '';
  }
});

// Navigation
function goNextSuggestion() {
  if (suggestions.value.length === 0)
    return;

  if (suggestionIndex.value === null) {
    suggestionIndex.value = 0;
  }
  else if (suggestions.value.length > suggestionIndex.value + 1) {
    suggestionIndex.value += 1;
  }
}

function goPrevSuggestion() {
  if (suggestions.value.length === 0)
    return;

  if (suggestionIndex.value === null) {
    suggestionIndex.value = suggestions.value.length - 1;
  }
  else if (suggestionIndex.value > 0) {
    suggestionIndex.value -= 1;
  }
}

function selectSuggestion(index: number) {
  const suggestion = suggestions.value[index];
  currentSuggestion.value = suggestion;
  emit('select', suggestion);
  textareaRef.value?.blur();
}

function onKeydown(event: KeyboardEvent) {
  const ARROW_DOWN = 'ArrowDown';
  const ARROW_UP = 'ArrowUp';
  const ENTER = 'Enter';

  switch (event.key) {
    case ARROW_DOWN:
      goNextSuggestion();
      break;
    case ARROW_UP:
      goPrevSuggestion();
      break;
    case ENTER:
      if (suggestions.value.length === 0 || suggestionIndex.value === null)
        return;

      {
        const suggestion = suggestions.value[suggestionIndex.value];
        currentSuggestion.value = suggestion;
        emit('select', suggestion);
      }
      textareaRef.value?.blur();
      event.preventDefault();
      break;
    default:
      break;
  }
}
</script>

<template>
  <div :class="cn()">
    <textarea
      ref="textareaRef"
      v-model="inputQuery"
      :class="cn('textarea')"
      rows="1"
      :placeholder
      :required
      autocomplete="off"
      @keydown="onKeydown"
      @blur="emit('blur')"
    />
    <ul :class="cn('items')">
      <li v-if="loading">
        <BaseLoader size="sm" :class="cn('loader')" />
      </li>
      <li
        v-for="(suggestion, index) in sliceSuggestions"
        :key="index"
        :class="cn('item')"
      >
        <label
          :class="cn('suggestion base-input-radio base-input-radio_size_sm', { active: suggestionIndex === index })"
          @mousedown.prevent="selectSuggestion(index)"
        >
          <input v-model="suggestionIndex" type="radio" :value="index">
          {{ suggestion }}
        </label>
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
.address-suggestions {
  position: relative;

  &__textarea {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 22px 14px;
    font-size: inherit;
    font-family: inherit;
    font-weight: inherit;
    line-height: 1.15;
    background-color: transparent;
    border: 1px solid #c4c4c4;
    border-radius: 2px;
    outline: none;
    transition: border-color 0.3s;
    resize: none;

    &:hover,
    &:focus {
      border-color: #000;
    }

    &::placeholder {
      color: #c4c4c4;
    }
  }

  &__items {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    z-index: 1;
    display: none;
    margin: 0;
    padding: 0;
    list-style-type: none;
    background-color: #fff;
    border: 1px solid #c4c4c4;
    border-top: none;
  }

  &__item {
    margin: 0;
    padding: 0;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    padding: 5px 10px;
    border: none;

    &_active {
      background-color: green;
    }
  }

  &:focus-within & {
    &__items {
      display: block;
    }
  }

  &__loader {
    margin: 10px;
  }

  @media screen and (max-width: 991px) {
    &__textarea {
      padding: 14px 24px;
      font-size: 16px;
    }
  }
}
</style>
